import { useEffect, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';

export function useKeyboard() {
  const {
    puzzle,
    selectedCell,
    selectCell,
    setNumber,
    clearCell,
    undo,
    redo,
    toggleNoteMode,
    isPaused,
    isComplete,
  } = useGameStore();

  // Determine grid size from puzzle length
  const gridSize = puzzle.length === 16 ? 4 : 9;
  const maxNumber = gridSize;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (isPaused || isComplete) return;

    // Number keys 1-9 (or 1-4 for beginner)
    if (e.key >= '1' && e.key <= String(maxNumber)) {
      setNumber(parseInt(e.key, 10));
      return;
    }

    // Arrow keys for navigation
    if (selectedCell !== null) {
      const row = Math.floor(selectedCell / gridSize);
      const col = selectedCell % gridSize;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (row > 0) selectCell((row - 1) * gridSize + col);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (row < gridSize - 1) selectCell((row + 1) * gridSize + col);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (col > 0) selectCell(row * gridSize + (col - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (col < gridSize - 1) selectCell(row * gridSize + (col + 1));
          break;
      }
    }

    // Delete/Backspace to clear cell
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      clearCell();
      return;
    }

    // Ctrl+Z for undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
      return;
    }

    // Ctrl+Shift+Z or Ctrl+Y for redo
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      redo();
      return;
    }

    // N for note mode toggle
    if (e.key === 'n' || e.key === 'N') {
      toggleNoteMode();
      return;
    }

    // Escape to deselect
    if (e.key === 'Escape') {
      selectCell(null);
      return;
    }
  }, [selectedCell, selectCell, setNumber, clearCell, undo, redo, toggleNoteMode, isPaused, isComplete, gridSize, maxNumber]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
